import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Loader,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  TextField,
} from '@vibe/core';
import {
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  differenceInDays,
  format,
} from 'date-fns';
import TimeTrackTopBar, { type PresetKey, computePresetRange } from './TimeTrackTopBar';
import TimeTrackTable from './TimeTrackTable';
import TimeTrackStack from './TimeTrackStack';
import WorklogListPopup from './WorklogListPopup';
import ItemPicker, { type PickedItem } from './ItemPicker';
import type { WorklogRow } from './TimeTrackTable';
import {
  getWorklogs,
  createWorklog,
  updateWorklog,
  deleteWorklog,
  type Worklog,
} from '../../api/worklog.api';
import { fetchItemsWithContextByIds, type MondayItemWithContext } from '../../api/monday.api';
import { getPeriodStatus, getTimesheetConfig } from '../../api/timesheet-approval.api';
import type { TimesheetConfig } from '../../types/timesheet-approval.types';
import { getWorklogCategories, type WorklogCategory } from '../../api/worklog-category.api';
import { combineHoursMinutes } from '../../utils/timesheet-capacity';
import { todayISO } from '../../utils/dates';
import { useUsersStore } from '../../stores/usersStore';
import { useScheduleStore } from '../../stores/scheduleStore';
import { useApprovalStore } from '../../stores/approvalStore';
import { fetchMondayUsers } from '../../api/monday.api';
import TimesheetSubmitButton from '../timesheet-approval/TimesheetSubmitButton';
import TimesheetStatusBanner from '../timesheet-approval/TimesheetStatusBanner';
import TimesheetSubmitDialog from '../timesheet-approval/TimesheetSubmitDialog';
import LogWorkDialog from '../scheduler/LogWorkDialog';
import '../../styles/timetracking.css';

// ─── Types ─────────────────────────────────────────────────────────────────────

type ViewMode = 'table' | 'stack';

interface DateRange {
  start: string;
  end: string;
}

export interface TimeTrackingWorkspaceProps {
  /** Current user's monday.com user ID */
  currentUserId: number | null;
  /** Whether the current user has admin privileges */
  isAdmin: boolean;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function defaultDateRange(weekStartsOn: 0 | 1 = 1): DateRange {
  const today = new Date();
  return {
    start: format(startOfWeek(today, { weekStartsOn }), 'yyyy-MM-dd'),
    end: format(endOfWeek(today, { weekStartsOn }), 'yyyy-MM-dd'),
  };
}

/** Map API Worklog to the flat row shape used by Table / Stack views. */
function toWorklogRow(
  w: Worklog,
  userMap?: Map<number, { name: string }>,
  itemContextMap?: Map<string, MondayItemWithContext>,
): WorklogRow {
  const numericUserId = Number(w.mondayUserId);
  const resolvedUser = !isNaN(numericUserId) ? userMap?.get(numericUserId) : undefined;
  const itemCtx = itemContextMap?.get(String(w.mondayItemId));
  return {
    id: w.id,
    date: w.date,
    monday_item_id: Number(w.mondayItemId) || 0,
    monday_user_id: w.mondayUserId,
    user_name: resolvedUser?.name,
    item_name: itemCtx?.name,
    board_name: itemCtx?.boardName,
    group_name: itemCtx?.groupTitle,
    hours: Number(w.hours) || 0,
    description: w.description ?? undefined,
    createdByUserId: w.createdByUserId ?? undefined,
  };
}

// ─── XLSX Export ────────────────────────────────────────────────────────────────

async function exportToXlsx(worklogs: WorklogRow[], dateRange: DateRange): Promise<void> {
  try {
    const {
      createWorkbook, addTitleRow, styleHeaderRow, styleDataRow,
      setColumnWidths, downloadWorkbook, formatDateForExcel, round,
    } = await import('../../utils/export-report-excel');

    const wb = createWorkbook();
    const ws = wb.addWorksheet('Worklogs');

    addTitleRow(ws, 'Worklogs', dateRange.start, dateRange.end);

    const headers = ['Date', 'Item', 'Board', 'Group', 'User', 'Hours', 'Description'];
    const headerRow = ws.addRow(headers);
    styleHeaderRow(ws, headerRow);
    setColumnWidths(ws, [12, 24, 22, 18, 18, 10, 30]);

    for (const w of worklogs) {
      const row = ws.addRow([
        formatDateForExcel(w.date),
        w.item_name ?? `Item #${w.monday_item_id}`,
        w.board_name ?? '',
        w.group_name ?? '',
        w.user_name || `User #${w.monday_user_id}`,
        round(w.hours),
        w.description ?? '',
      ]);
      styleDataRow(row);
      row.getCell(6).alignment = { horizontal: 'center', vertical: 'middle' };
      row.getCell(6).numFmt = '0.00';
    }

    await downloadWorkbook(wb, `worklogs_${dateRange.start}_${dateRange.end}.xlsx`);
  } catch (err) {
    console.error('Excel export failed:', err);
  }
}

// ─── Worklog Form Modal ────────────────────────────────────────────────────────

interface WorklogFormData {
  mondayItemId: string;
  scheduleItemId: string;
  date: string;
  hoursInput: string;
  minutesInput: string;
  description: string;
}

const EMPTY_FORM: WorklogFormData = {
  mondayItemId: '',
  scheduleItemId: '',
  date: todayISO(),
  hoursInput: '',
  minutesInput: '',
  description: '',
};

// ─── Worklog List Popup State ──────────────────────────────────────────────────

interface CellPopupState {
  userId: string;
  userName: string;
  date: string;
  worklogs: WorklogRow[];
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Main container for the workspace-level Time Tracking screen.
 *
 * Displays the user's own worklog entries in table/stack views.
 */
const TimeTrackingWorkspace: React.FC<TimeTrackingWorkspaceProps> = ({
  currentUserId,
  isAdmin,
}) => {
  // ─── Stores ──────────────────────────────────────────────────────────────
  const users = useUsersStore((s) => s.users);
  const setUsers = useUsersStore((s) => s.setUsers);
  const schedulerFirstDay = useScheduleStore((s) => s.firstDayOfWeek);

  // Build a user map for O(1) name lookups
  const userMap = useMemo(() => {
    const map = new Map<number, { name: string }>();
    for (const u of users) {
      map.set(u.id, { name: u.name });
    }
    return map;
  }, [users]);

  // Fetch users on mount if store is empty
  useEffect(() => {
    if (users.length === 0) {
      fetchMondayUsers()
        .then((fetched) => setUsers(fetched))
        .catch((err) => console.error('Failed to fetch users:', err));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Approval store ────────────────────────────────────────────────────
  const mySubmission = useApprovalStore((s) => s.mySubmission);
  const setMySubmission = useApprovalStore((s) => s.setMySubmission);
  const submitDialogOpen = useApprovalStore((s) => s.submitDialogOpen);
  const setSubmitDialogOpen = useApprovalStore((s) => s.setSubmitDialogOpen);

  // ─── Timesheet config (approval period settings) ─────────────────────
  const [tsConfig, setTsConfig] = useState<TimesheetConfig | null>(null);

  useEffect(() => {
    getTimesheetConfig()
      .then(setTsConfig)
      .catch(() => setTsConfig(null));
  }, []);

  // When approval is enabled, use timesheet config's week_start_day; otherwise use monday.com setting
  const WEEK_START_MAP: Record<string, 0 | 1> = { sunday: 0, monday: 1, saturday: 0 };
  const isApprovalEnabled = tsConfig?.approval_enabled ?? true;
  const firstDayOfWeek: 0 | 1 = isApprovalEnabled && tsConfig
    ? (WEEK_START_MAP[tsConfig.week_start_day] ?? 1)
    : schedulerFirstDay;

  // ─── Preset tracking ─────────────────────────────────────────────────
  const [activePreset, setActivePreset] = useState<PresetKey>('this_week');

  // ─── Worklog state ──────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [dateRange, setDateRange] = useState<DateRange>(() => defaultDateRange(schedulerFirstDay));

  // Recalculate date range when firstDayOfWeek changes (e.g. after tsConfig loads)
  const prevFirstDayRef = React.useRef(firstDayOfWeek);
  useEffect(() => {
    if (firstDayOfWeek !== prevFirstDayRef.current) {
      prevFirstDayRef.current = firstDayOfWeek;
      setDateRange(defaultDateRange(firstDayOfWeek));
    }
  }, [firstDayOfWeek]);

  /** Check if the current date range matches a submittable period (weekly or monthly). */
  const isSubmittablePeriod = useMemo(() => {
    if (!isApprovalEnabled) return false;
    const periodType = tsConfig?.period_type ?? 'weekly';
    const days = differenceInDays(new Date(dateRange.end), new Date(dateRange.start)) + 1;

    if (periodType === 'weekly') {
      return days >= 6 && days <= 7;
    }
    if (periodType === 'monthly') {
      return days >= 28 && days <= 31;
    }
    return false;
  }, [dateRange, tsConfig, isApprovalEnabled]);

  const submissionPeriodLabel = (tsConfig?.period_type ?? 'weekly') === 'monthly' ? 'month' : 'week';

  /** Whether the current period is locked (submitted or approved). */
  const isPeriodLocked = useMemo(() => {
    if (!mySubmission) return false;
    const { status } = mySubmission;
    // Always lock when approved; lock on submitted only if lock_on_approval is true (default)
    if (status === 'approved') return true;
    if (status === 'submitted' && (tsConfig?.lock_on_approval ?? true)) return true;
    return false;
  }, [mySubmission, tsConfig]);

  const [rawWorklogs, setRawWorklogs] = useState<Worklog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ─── Item context resolution (name + board + group) ─────────────────────
  const [itemContextMap, setItemContextMap] = useState<Map<string, MondayItemWithContext>>(new Map());

  useEffect(() => {
    if (rawWorklogs.length === 0) {
      setItemContextMap(new Map());
      return;
    }
    const uniqueItemIds = [...new Set(rawWorklogs.map((w) => String(w.mondayItemId)))];
    const missingIds = uniqueItemIds.filter((id) => !itemContextMap.has(id));
    if (missingIds.length === 0) return;

    fetchItemsWithContextByIds(missingIds)
      .then((items) => {
        setItemContextMap((prev) => {
          const next = new Map(prev);
          for (const item of items) {
            next.set(String(item.id), item);
          }
          return next;
        });
      })
      .catch((err) => console.error('Failed to fetch item context:', err));
  }, [rawWorklogs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive WorklogRow[] reactively from raw data + userMap + itemContextMap
  const worklogs = useMemo(
    () => rawWorklogs.map((w) => toWorklogRow(w, userMap, itemContextMap)),
    [rawWorklogs, userMap, itemContextMap],
  );

  // ─── Modal state ────────────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWorklogId, setEditingWorklogId] = useState<string | null>(null);
  const [formData, setFormData] = useState<WorklogFormData>(EMPTY_FORM);
  const [selectedItem, setSelectedItem] = useState<PickedItem | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // ─── Worklog categories ────────────────────────────────────────────────
  const [categories, setCategories] = useState<WorklogCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  useEffect(() => {
    getWorklogCategories(false).then((cats) => {
      setCategories(cats);
      const def = cats.find((c) => c.isDefault) ?? cats[0];
      if (def) setSelectedCategoryId(def.id);
    }).catch(() => {});
  }, []);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // ─── Worklog List Popup state ──────────────────────────────────────────
  const [cellPopup, setCellPopup] = useState<CellPopupState | null>(null);

  // ─── Date navigation ─────────────────────────────────────────────────────

  const handleDatePrev = useCallback(() => {
    setDateRange((prev) => {
      const days = differenceInDays(new Date(prev.end), new Date(prev.start)) + 1;
      // Navigate by month if viewing a monthly range
      if (days >= 28 && days <= 31) {
        const prevMonth = subMonths(new Date(prev.start), 1);
        return {
          start: format(startOfMonth(prevMonth), 'yyyy-MM-dd'),
          end: format(endOfMonth(prevMonth), 'yyyy-MM-dd'),
        };
      }
      const start = subWeeks(new Date(prev.start), 1);
      const end = subWeeks(new Date(prev.end), 1);
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
      };
    });
  }, []);

  const handleDateNext = useCallback(() => {
    setDateRange((prev) => {
      const days = differenceInDays(new Date(prev.end), new Date(prev.start)) + 1;
      if (days >= 28 && days <= 31) {
        const nextMonth = addMonths(new Date(prev.start), 1);
        return {
          start: format(startOfMonth(nextMonth), 'yyyy-MM-dd'),
          end: format(endOfMonth(nextMonth), 'yyyy-MM-dd'),
        };
      }
      const start = addWeeks(new Date(prev.start), 1);
      const end = addWeeks(new Date(prev.end), 1);
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
      };
    });
  }, []);

  // ─── Date range label ─────────────────────────────────────────────────────

  const dateRangeLabel = useMemo(() => {
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
  }, [dateRange]);

  // ─── Fetch worklogs (current user) ────────────────────────────────────

  const fetchWorklogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const userId = String(currentUserId ?? 0);
      const data = await getWorklogs({
        userId,
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      setRawWorklogs(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load worklogs';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, currentUserId]);

  useEffect(() => {
    fetchWorklogs();
  }, [fetchWorklogs]);

  // ─── Fetch approval status for current period ──────────────────────────

  useEffect(() => {
    if (!currentUserId || !isSubmittablePeriod) {
      setMySubmission(null);
      return;
    }
    const userId = String(currentUserId);
    getPeriodStatus(userId, dateRange.start, dateRange.end)
      .then((submission) => setMySubmission(submission))
      .catch(() => setMySubmission(null));
  }, [dateRange.start, dateRange.end, currentUserId, isSubmittablePeriod, setMySubmission]);

  const handleSubmitSuccess = useCallback(
    (submission: typeof mySubmission) => {
      setMySubmission(submission);
      setSubmitDialogOpen(false);
    },
    [setMySubmission, setSubmitDialogOpen],
  );

  // ─── Modal handlers ────────────────────────────────────────────────────

  const openAddModal = useCallback(() => {
    setEditingWorklogId(null);
    setFormData({ ...EMPTY_FORM, date: todayISO() });
    setSelectedItem(null);
    setFormError(null);
    setIsModalOpen(true);
  }, []);

  /** Open add modal pre-filled with a specific date (from matrix cell click) */
  const openAddModalForCell = useCallback(
    (_userId: string, date: string) => {
      setEditingWorklogId(null);
      setFormData({ ...EMPTY_FORM, date });
      setSelectedItem(null);
      setFormError(null);
      setIsModalOpen(true);
    },
    [],
  );

  /** Open add modal pre-filled with a date (from stack column "+" button) */
  const handleAddForDay = useCallback((date: string) => {
    setEditingWorklogId(null);
    setFormData({ ...EMPTY_FORM, date });
    setSelectedItem(null);
    setFormError(null);
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback(
    (id: string) => {
      const worklog = worklogs.find((w) => w.id === id);
      if (!worklog) return;
      setEditingWorklogId(id);
      const wlH = Math.floor(worklog.hours);
      const wlM = Math.round((worklog.hours - wlH) * 60);
      setFormData({
        mondayItemId: String(worklog.monday_item_id),
        scheduleItemId: '',
        date: worklog.date.slice(0, 10),
        hoursInput: String(wlH),
        minutesInput: String(wlM),
        description: worklog.description ?? '',
      });
      setSelectedItem({
        id: String(worklog.monday_item_id),
        name: worklog.item_name ?? `Item #${worklog.monday_item_id}`,
      });
      setFormError(null);
      setIsModalOpen(true);
      // Close cell popup if open
      setCellPopup(null);
    },
    [worklogs],
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingWorklogId(null);
    setFormData(EMPTY_FORM);
    setSelectedItem(null);
    setFormError(null);
  }, []);

  const handleFormChange = useCallback(
    (field: keyof WorklogFormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // ─── Cell click handler (matrix view) ─────────────────────────────────

  const handleCellClick = useCallback(
    (userId: string, date: string, cellWorklogs: WorklogRow[]) => {
      const firstWorklog = cellWorklogs[0];
      const userName =
        firstWorklog?.user_name || `User #${userId}`;
      setCellPopup({ userId, userName, date, worklogs: cellWorklogs });
    },
    [],
  );

  // ─── Save (create or update) ───────────────────────────────────────────

  const handleSave = useCallback(async () => {
    setFormError(null);

    const parsedHours = combineHoursMinutes(formData.hoursInput, formData.minutesInput);
    if (parsedHours <= 0) {
      setFormError('Time must be greater than zero.');
      return;
    }
    if (parsedHours > 24) {
      setFormError('Time cannot exceed 24 hours.');
      return;
    }
    if (!formData.date) {
      setFormError('Date is required.');
      return;
    }
    const itemId = selectedItem?.id || formData.mondayItemId;
    if (!itemId) {
      setFormError('Please select an item.');
      return;
    }

    setIsSaving(true);
    try {
      if (editingWorklogId) {
        await updateWorklog(editingWorklogId, {
          date: formData.date,
          hours: parsedHours,
          description: formData.description.trim() || undefined,
          worklogCategoryId: selectedCategoryId || undefined,
        });
      } else {
        await createWorklog({
          mondayItemId: itemId,
          mondayBoardId: selectedItem?.boardId ?? '0',
          mondayUserId: String(currentUserId ?? 0),
          date: formData.date,
          hours: parsedHours,
          description: formData.description.trim() || null,
          worklogCategoryId: selectedCategoryId || undefined,
        });
      }
      closeModal();
      await fetchWorklogs();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save worklog';
      setFormError(message);
    } finally {
      setIsSaving(false);
    }
  }, [
    formData,
    editingWorklogId,
    selectedItem,
    selectedCategoryId,
    closeModal,
    fetchWorklogs,
    currentUserId,
  ]);

  // ─── Delete ────────────────────────────────────────────────────────────

  const handleDeleteRequest = useCallback((id: string) => {
    setDeleteConfirmId(id);
    setCellPopup(null); // Close cell popup
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmId) return;
    try {
      await deleteWorklog(deleteConfirmId);
      setDeleteConfirmId(null);
      await fetchWorklogs();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete worklog';
      setError(message);
      setDeleteConfirmId(null);
    }
  }, [deleteConfirmId, fetchWorklogs]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  // ─── Export ────────────────────────────────────────────────────────────

  const handleExport = useCallback(() => {
    exportToXlsx(worklogs, dateRange);
  }, [worklogs, dateRange]);

  // ─── Render ────────────────────────────────────────────────────────────

  const isEditing = editingWorklogId !== null;

  return (
    <div className="rp-tt-workspace">
      {/* Top bar */}
      <TimeTrackTopBar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        dateRangeLabel={dateRangeLabel}
        onDatePrev={handleDatePrev}
        onDateNext={handleDateNext}
        onAddWorklog={isPeriodLocked ? undefined : openAddModal}
        onExport={handleExport}
        activePreset={activePreset}
        onPresetChange={setActivePreset}
        rightSlot={
          isSubmittablePeriod ? (
            <TimesheetSubmitButton
              submission={mySubmission}
              currentUserId={String(currentUserId ?? 0)}
              isAdmin={isAdmin}
            />
          ) : undefined
        }
      />

      {/* Approval status banner (submittable period only) */}
      {isSubmittablePeriod && (
        <TimesheetStatusBanner submission={mySubmission} />
      )}

      {/* Hint when viewing a non-submittable period */}
      {isApprovalEnabled && !isSubmittablePeriod && (
        <div className="rp-ts-approval-banner">
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            Timesheet submission period is configured as <b>{submissionPeriodLabel}ly</b>.
            Switch to a {submissionPeriodLabel} view to submit your timesheet for approval.
          </Text>
        </div>
      )}

      {/* Main content area */}
      <div className="rp-tt-workspace__content">
        {isLoading ? (
          <div className="rp-tt-workspace__loading">
            <Loader size={Loader.sizes.MEDIUM} />
          </div>
        ) : error ? (
          <div className="rp-tt-workspace__error">
            <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
              {error}
            </Text>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              onClick={fetchWorklogs}
              style={{ marginTop: '8px' }}
            >
              Retry
            </Button>
          </div>
        ) : viewMode === 'table' ? (
          <TimeTrackTable
            worklogs={worklogs}
            onEdit={openEditModal}
            onDelete={handleDeleteRequest}
            isLoading={isLoading}
            dateRangeStart={dateRange.start}
            dateRangeEnd={dateRange.end}
            onAddWorklogForCell={openAddModalForCell}
            onCellClick={handleCellClick}
            readOnly={isPeriodLocked}
          />
        ) : (
          <TimeTrackStack
            worklogs={worklogs}
            onEdit={openEditModal}
            onDelete={handleDeleteRequest}
            dateRangeStart={dateRange.start}
            dateRangeEnd={dateRange.end}
            onAddForDay={handleAddForDay}
            readOnly={isPeriodLocked}
          />
        )}
      </div>

      {/* ── Worklog List Popup (matrix cell detail) ─────────────────────── */}
      {cellPopup && (
        <WorklogListPopup
          worklogs={cellPopup.worklogs}
          date={cellPopup.date}
          userName={cellPopup.userName}
          onEdit={openEditModal}
          onDelete={handleDeleteRequest}
          onClose={() => setCellPopup(null)}
          readOnly={isPeriodLocked}
        />
      )}

      {/* Add Worklog – uses the same LogWorkDialog as the Scheduler */}
      {isModalOpen && !isEditing && (
        <LogWorkDialog
          currentUserId={String(currentUserId ?? 0)}
          initialDate={formData.date || undefined}
          onClose={closeModal}
          onWorkLogged={async () => {
            closeModal();
            await fetchWorklogs();
          }}
        />
      )}

      {/* Edit Worklog Modal */}
      <Modal
        show={isModalOpen && isEditing}
        onClose={closeModal}
        title="Edit Worklog"
      >
        <ModalHeader title="Edit Worklog" />
        <ModalContent>
          <div className="rp-tt-modal__form">
            <div className="rp-tt-modal__field">
              <label className="rp-tt-modal__label">Item</label>
              <ItemPicker
                value={selectedItem}
                onChange={(item) => {
                  setSelectedItem(item);
                  handleFormChange('mondayItemId', item?.id ?? '');
                }}
                placeholder="Search for an item…"
                disabled
              />
            </div>

            <div className="rp-tt-modal__field">
              <label className="rp-tt-modal__label">Date</label>
              <TextField
                type={TextField.types.DATE}
                size={TextField.sizes.SMALL}
                value={formData.date}
                onChange={(value: string) =>
                  handleFormChange('date', value)
                }
              />
            </div>

            <div className="rp-tt-modal__field">
              <label className="rp-tt-modal__label">Time Spent</label>
              <div className="rp-tt-modal__time-row">
                <input
                  type="number"
                  min={0}
                  max={24}
                  value={formData.hoursInput}
                  onChange={(e) => handleFormChange('hoursInput', e.target.value)}
                  className="rp-tt-modal__time-input"
                  placeholder="0"
                />
                <span className="rp-tt-modal__time-unit">h</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  step={5}
                  value={formData.minutesInput}
                  onChange={(e) => handleFormChange('minutesInput', e.target.value)}
                  className="rp-tt-modal__time-input"
                  placeholder="0"
                />
                <span className="rp-tt-modal__time-unit">m</span>
              </div>
            </div>

            <div className="rp-tt-modal__field">
              <label className="rp-tt-modal__label">
                Description (optional)
              </label>
              <TextField
                size={TextField.sizes.SMALL}
                value={formData.description}
                onChange={(value: string) =>
                  handleFormChange('description', value)
                }
                placeholder="What did you work on?"
              />
            </div>

            {categories.length > 1 && (
              <div className="rp-tt-modal__field">
                <label className="rp-tt-modal__label">Category</label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="rp-tt-modal__category-select"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formError && (
              <Text
                type={Text.types.TEXT2}
                style={{ color: '#e2445c', marginTop: '4px' }}
              >
                {formError}
              </Text>
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          <Flex gap={Flex.gaps.SMALL} justify={Flex.justify.END}>
            <Button
              kind={Button.kinds.TERTIARY}
              size={Button.sizes.SMALL}
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              size={Button.sizes.SMALL}
              onClick={handleSave}
              loading={isSaving}
            >
              Update
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        show={deleteConfirmId !== null}
        onClose={handleDeleteCancel}
        title="Delete Worklog"
      >
        <ModalHeader title="Delete Worklog" />
        <ModalContent>
          <Text type={Text.types.TEXT1} style={{ whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}>
            Are you sure you want to delete this worklog entry? This action cannot be undone.
          </Text>
        </ModalContent>
        <ModalFooter>
          <Flex gap={Flex.gaps.SMALL} justify={Flex.justify.END}>
            <Button
              kind={Button.kinds.TERTIARY}
              size={Button.sizes.SMALL}
              onClick={handleDeleteCancel}
            >
              Cancel
            </Button>
            <Button
              size={Button.sizes.SMALL}
              color={Button.colors.NEGATIVE}
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </Flex>
        </ModalFooter>
      </Modal>

      {/* Submit / Resubmit timesheet dialog */}
      {submitDialogOpen && (
        <TimesheetSubmitDialog
          currentUserId={String(currentUserId ?? 0)}
          dateRange={dateRange}
          onSuccess={handleSubmitSuccess}
          onClose={() => setSubmitDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default TimeTrackingWorkspace;
